// @ts-nocheck
/**
 * Helpers y utilidades para usar la configuración de la aplicación
 * Este archivo proporciona funciones utilitarias para acceder y manipular
 * la configuración de manera más sencilla en los componentes
 */

import configProject from './appConfig';
const config = configProject;

// Debug: verificar que config se cargó correctamente
console.log('[CONFIG] Config loaded:', {
    hasConfig: !!config,
    hasColors: !!config?.colors,
    colorKeys: config?.colors ? Object.keys(config.colors) : [],
    config: config
});

// ===========================================
// HELPERS PARA ASSETS
// ===========================================

/**
 * Obtiene la URL completa de un logo
 * @param logoType - Tipo de logo (main, icon, white, dark, small)
 * @returns URL del logo
 */
export const getLogo = (logoType: keyof typeof config.assets.logos = 'main'): string => {
    return config.assets.logos[logoType] || config.assets.logos.main;
};

export const typeUser = (type: 'one' | 'two') => {
    return type === 'one' ? config.company.typeUserOne : config.company.typeUserTwo;
};

/**
 * Obtiene la información del favicon
 * @param size - Tamaño del favicon (16, 32, apple)
 * @returns URL del favicon
 */
export const getFavicon = (size: '16' | '32' | 'apple' = '32'): string => {
    switch (size) {
        case '16':
            return config.assets.favicons.favicon16;
        case '32':
            return config.assets.favicons.favicon32;
        case 'apple':
            return config.assets.favicons.appleTouchIcon;
        default:
            return config.assets.favicons.favicon32;
    }
};

/**
 * Obtiene la configuración de tipografía
 * @returns Objeto con la configuración de fuente
 */
export const getTypographyConfig = () => {
    return (
        config.typography || {
            fontFamily: 'Plus Jakarta Sans',
            googleFontsUrl: '',
            fallbackFonts: ['system-ui', 'Avenir', 'Helvetica', 'Arial', 'sans-serif'],
            isGoogleFont: false
        }
    );
};

/**
 * Obtiene la familia de fuentes completa (principal + fallback)
 * @returns String con la familia de fuentes para CSS
 */
export const getFontFamily = (): string => {
    const typography = getTypographyConfig();
    const fallback = typography.fallbackFonts?.join(', ') || 'system-ui, sans-serif';
    return `${typography.fontFamily}, ${fallback}`;
};

// ===========================================
// HELPERS PARA COLORES
// ===========================================

/**
 * Obtiene un color del tema principal
 * @param shade - Tono del color (main, light, dark)
 * @returns Color en formato hex
 */
export const getColor = (shade: 'light' | 'dark' | 'loginColors' = 'light'): string => {
    return (config.colors as ProjectColorsConfig)[shade];
};

/**
 * Obtiene todas las imágenes de fondo de login configuradas
 * Busca loginBackground, loginBackground1, loginBackground2, etc.
 * @returns Array de URLs de imágenes de fondo
 */
export const getLoginBackgrounds = (): string[] => {
    const images = config.assets.images || {};
    const backgrounds: string[] = [];

    // Buscar loginBackground (sin número)
    if (images.loginBackground) {
        backgrounds.push(images.loginBackground);
    }

    // Buscar loginBackground1, loginBackground2, etc.
    let index = 1;
    while (images[`loginBackground${index}`]) {
        backgrounds.push(images[`loginBackground${index}`]);
        index++;
    }

    return backgrounds;
};

/**
 * Obtiene una imagen de fondo de login aleatoria
 * Si hay múltiples imágenes configuradas, selecciona una aleatoria
 * @returns URL de una imagen de fondo o string vacío si no hay imágenes
 */
export const getLoginBackground = (): string => {
    const backgrounds = getLoginBackgrounds();
    if (backgrounds.length === 0) {
        return '';
    }
    if (backgrounds.length === 1) {
        return backgrounds[0];
    }
    // Seleccionar una imagen aleatoria
    const randomIndex = Math.floor(Math.random() * backgrounds.length);
    return backgrounds[randomIndex];
};

/**
 * Obtiene la imagen de seguro por defecto para modo claro
 * @returns URL de la imagen o string vacío si no está configurada
 */
export const getDefaultInsuranceImageLight = (): string => {
    return config.assets.images?.defaultInsuranceImageLight || '';
};

/**
 * Obtiene la imagen de seguro por defecto para modo oscuro
 * @returns URL de la imagen o string vacío si no está configurada
 */
export const getDefaultInsuranceImageDark = (): string => {
    return config.assets.images?.defaultInsuranceImageDark || '';
};

/**
 * Obtiene todos los colores del tema para un modo específico
 * @param mode - Modo del tema ('light' | 'dark')
 * @returns Objeto ThemeColors completo
 */
export const getThemeColors = (mode: 'light' | 'dark' = 'light') => {
    console.log('[CONFIG] getThemeColors called with mode:', mode);
    console.log('[CONFIG] config.colors:', config?.colors);

    if (!config || !config.colors) {
        console.error('[CONFIG] ❌ Config or config.colors is undefined!');
        return undefined;
    }

    const colors = (config.colors as ProjectColorsConfig)[mode];
    console.log('[CONFIG] Returning colors for mode', mode, ':', colors);
    return colors;
};

/**
 * Obtiene un color del tema secundario
 * @param shade - Tono del color (main, light, dark)
 * @returns Color en formato hex
 */
export const getSecondaryColor = (shade: 'main' | 'light' | 'dark' = 'main'): string => {
    return config.colors[shade];
};

// ===========================================
// HELPERS PARA INFORMACIÓN DE LA EMPRESA
// ===========================================

/**
 * Obtiene el nombre de la empresa
 * @param full - Si se debe retornar el nombre completo
 * @returns Nombre de la empresa
 */
export const getCompanyName = (full: boolean = false): string => {
    return full ? config.company.fullName : config.company.name;
};

/**
 * Obtiene la información de contacto
 * @returns Objeto con información de contacto
 */
export const getContactInfo = () => {
    return config.company.contact;
};

/**
 * Obtiene una URL específica de la empresa
 * @param urlType - Tipo de URL (website, support, documentation, etc.)
 * @returns URL solicitada
 */
export const getCompanyUrl = (urlType: keyof typeof config.company.urls): string => {
    return config.company.urls[urlType];
};

export const getSidebarConfig = () => {
    return config.layout.sidebar;
};

/**
 * Obtiene la configuración del header
 * @returns Configuración del header
 */
export const getHeaderConfig = () => {
    return config.layout.header;
};

export const getVersion = () => {
    return config.company.version;
};

/**
 * Actualiza dinámicamente el título de la página usando getCompanyName
 * @param customTitle - Título personalizado (opcional)
 */
export const updatePageTitle = (customTitle?: string) => {
    const companyName = getCompanyName();
    const title = customTitle ? `${customTitle} - ${companyName}` : companyName;
    document.title = title;
};

/**
 * Actualiza dinámicamente el favicon usando getLogo con tipo "icon"
 * @param customFavicon - Favicon personalizado (opcional)
 */
export const updateFavicon = (customFavicon?: string) => {
    const faviconPath = customFavicon || getLogo('icon');

    // Remover favicons existentes
    const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
    existingFavicons.forEach((link) => link.remove());

    // Agregar nuevo favicon SVG
    const link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/svg+xml';
    link.href = faviconPath;
    document.head.appendChild(link);

    // Mantener apple-touch-icon si existe
    const existingAppleIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (existingAppleIcon) {
        // Mantener el existente
    } else if (config.assets.favicons.appleTouchIcon) {
        // Agregar apple-touch-icon si no existe
        const appleLink = document.createElement('link');
        appleLink.rel = 'apple-touch-icon';
        appleLink.sizes = '180x180';
        appleLink.href = config.assets.favicons.appleTouchIcon;
        document.head.appendChild(appleLink);
    }
};

export async function loadComponent() {
    try {
        const animations = import.meta.glob('../animations/**/*.vue');

        if (config?.assets.animations?.jotform && config?.assets.animations?.jotform !== '') {
            const animationPath = `../animations/${config.assets.animations.jotform}.vue`;

            const loadFn = animations[animationPath];

            if (loadFn) {
                const module: any = await loadFn();
                return module.default;
            } else {
                console.info(`⚠️ Animación no encontrada: ${animationPath}`);
            }
        }

        const module = await import(`@/skeletors/jotform/JotformRenderer.vue`);
        return module.default;
    } catch (err) {
        console.info(`❌ error loading component: ${err}`);

        const module = await import(`@/skeletors/jotform/JotformRenderer.vue`);
        return module.default;
    }
}

// ===========================================
// EXPORTACIÓN DE UTILIDADES COMUNES
// ===========================================

export const configUtils = {
    // Assets
    getLogo,
    getFavicon,

    // Colores
    getColor,

    // Empresa
    getCompanyName,
    getContactInfo,
    getCompanyUrl,

    // Layout
    getSidebarConfig,
    getHeaderConfig,

    // Version
    getVersion,

    // Tipografía
    getTypographyConfig,
    getFontFamily,

    // HTML
    updatePageTitle,
    updateFavicon,

    //animaciones
    loadComponent
};

export default configUtils;
